import { motion } from "framer-motion";
import { X, Check, TrendingDown, TrendingUp, DollarSign, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";

const PainSection = () => {
  const { t } = useTranslation();
  
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <section 
      className="dark-section py-20 lg:py-32"
      data-testid="pain-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div 
          {...fadeInUp}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="pain-headline"
          >
            {t('pain.title')} <span className="text-red-400">{t('pain.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-slate-400">
            {t('pain.subtitle')}
          </p>
        </motion.div>

        {/* Comparison cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Discount Strategy - The Problem */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="absolute -top-3 left-6 px-4 py-1 bg-red-500 text-white text-sm font-semibold rounded-full">
              {t('pain.discountTrap.badge')}
            </div>
            <div className="bg-slate-800/50 border border-red-500/30 rounded-3xl p-8 h-full">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="w-7 h-7 text-red-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('pain.discountTrap.title')}
                  </h3>
                  <p className="text-red-400 text-sm">{t('pain.discountTrap.subtitle')}</p>
                </div>
              </div>

              <ul className="space-y-4">
                {t('pain.discountTrap.problems', { returnObjects: true }).map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <div className="mt-1 w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <X className="w-3 h-3 text-red-400" />
                    </div>
                    <span className="text-slate-300">{item}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Result */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-red-400" />
                  <span className="text-slate-400">
                    {t('pain.discountTrap.result')} <span className="text-red-400 font-semibold">{t('pain.discountTrap.resultText')}</span>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Loyalty Strategy - The Solution */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -top-3 left-6 px-4 py-1 bg-emerald-500 text-white text-sm font-semibold rounded-full">
              {t('pain.loyaltyLoop.badge')}
            </div>
            <div className="bg-slate-800/50 border border-emerald-500/30 rounded-3xl p-8 h-full">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('pain.loyaltyLoop.title')}
                  </h3>
                  <p className="text-emerald-400 text-sm">{t('pain.loyaltyLoop.subtitle')}</p>
                </div>
              </div>

              <ul className="space-y-4">
                {t('pain.loyaltyLoop.benefits', { returnObjects: true }).map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                    className="flex items-start gap-3"
                  >
                    <div className="mt-1 w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-slate-300">{item}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Result */}
              <div className="mt-8 pt-6 border-t border-slate-700">
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5 text-emerald-400" />
                  <span className="text-slate-400">
                    {t('pain.loyaltyLoop.result')} <span className="text-emerald-400 font-semibold">{t('pain.loyaltyLoop.resultText')}</span>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {t('pain.bottomMessage')}
            <span className="text-amber-400 font-semibold"> {t('pain.bottomMessageHighlight')}</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PainSection;
